import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { submitContact } from "@/lib/contatos.functions";
import { Mail } from "lucide-react";

export const Route = createFileRoute("/_main/contato")({
  component: Contato,
});

function Contato() {
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(false);

  const [assunto, setAssunto] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!assunto) {
      toast.error("Por favor, selecione um assunto.");
      return;
    }
    
    setLoading(true);
    const formData = new FormData(e.currentTarget);

    try {
      await submitContact({
        data: {
          nome: formData.get("nome") as string,
          email: formData.get("email") as string,
          telefone: (formData.get("telefone") as string) || "",
          assunto: assunto,
          mensagem: formData.get("mensagem") as string,
          user_id: user?.id,
        }
      });

      toast.success("Mensagem enviada com sucesso!");
      (e.target as HTMLFormElement).reset();
      setAssunto("");
    } catch (err: any) {
      toast.error(err.message || "Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>Contato | Gopage Fornecedores</title>
        <meta name="description" content="Fale conosco. Tire suas dúvidas sobre a lista de fornecedores Gopage." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-brand-dark text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/5 pointer-events-none"></div>
        <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Fale com a gente</h1>
          <p className="text-xl text-gray-300">
            Dúvidas, sugestões ou pedidos sobre a lista? Nossa equipe responde em até 2 dias úteis.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16 -mt-10 relative z-20">
        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto items-start">
          {/* Info Cards */}
          <div className="space-y-6 lg:mt-0">
            <div className="bg-[#F7F9F5] p-8 rounded-3xl border border-border shadow-sm">
              <div className="w-12 h-12 bg-primary rounded-2xl flex items-center justify-center mb-6">
                <Mail className="w-6 h-6 text-brand-dark" />
              </div>
              <h3 className="text-lg font-bold mb-2">E-mail</h3>
              <p className="text-muted-foreground text-sm mb-4">Envie um e-mail a qualquer momento.</p>
              <a href="mailto:contato@gopage.bio" className="text-brand-dark font-bold hover:underline">
                contato@gopage.bio
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2 bg-white p-8 md:p-12 rounded-3xl border border-border shadow-xl">
            <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
              Envie uma mensagem
              <span className="h-px flex-1 bg-border hidden sm:block"></span>
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold ml-1">Seu Nome *</label>
                  <Input 
                    name="nome" 
                    placeholder="Como podemos te chamar?" 
                    required 
                    defaultValue={profile?.nome || ""}
                    className="h-12 rounded-xl focus:ring-primary"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold ml-1">Seu E-mail *</label>
                  <Input 
                    name="email" 
                    type="email" 
                    placeholder="seu@email.com" 
                    required 
                    defaultValue={user?.email || ""}
                    className="h-12 rounded-xl focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold ml-1">WhatsApp</label>
                  <Input 
                    name="telefone" 
                    placeholder="(00) 00000-0000" 
                    defaultValue={profile?.telefone || ""}
                    className="h-12 rounded-xl focus:ring-primary"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold ml-1">Assunto *</label>
                  <Select value={assunto} onValueChange={setAssunto} required>
                    <SelectTrigger className="h-12 rounded-xl focus:ring-primary bg-white border-input">
                      <SelectValue placeholder="Selecione o assunto" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Dúvida sobre o acesso">Dúvida sobre o acesso</SelectItem>
                      <SelectItem value="Quero anunciar minha empresa">Quero anunciar minha empresa</SelectItem>
                      <SelectItem value="Sugerir um fornecedor">Sugerir um fornecedor</SelectItem>
                      <SelectItem value="Reportar informação incorreta">Reportar informação incorreta</SelectItem>
                      <SelectItem value="Remover minha empresa da lista">Remover minha empresa da lista</SelectItem>
                      <SelectItem value="Parceria">Parceria</SelectItem>
                      <SelectItem value="Outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold ml-1">Mensagem *</label>
                <Textarea 
                  name="mensagem" 
                  placeholder="Descreva sua dúvida ou solicitação com detalhes..." 
                  required 
                  className="min-h-[150px] rounded-2xl focus:ring-primary p-4"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full sm:w-auto h-14 px-12 rounded-2xl bg-brand-dark text-white font-bold hover:bg-brand-dark/90 text-lg transition-all shadow-lg hover:shadow-primary/20"
              >
                {loading ? "Enviando..." : "Enviar Mensagem"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
